const REVEAL = '[data-ov-reveal]';
const PARALLAX = '[data-ov-parallax]';
const EXCLUDED = 'footer, dialog, [data-editor-preview], .block-editor-block-list__layout';

/** Progressive enhancement: no HTML/CSS starts hidden, and cancelling restores native styles. */
export class SiteMotion {
    constructor(document) {
        this.document = document;
        this.window = document.defaultView;
        this.seen = new WeakSet();
        this.reveals = new Map();
        this.frames = new Map();
        this.activeFrames = new Set();
        this.frameRequest = null;
        this.started = false;
        this.onFocus = this.onFocus.bind(this);
        this.onPreference = this.onPreference.bind(this);
        this.onVisibility = this.onVisibility.bind(this);
        this.onResize = this.onResize.bind(this);
        this.scheduleFrame = this.scheduleFrame.bind(this);
    }

    start() {
        const win = this.window;
        if (this.started || !win.IntersectionObserver || !win.Element?.prototype.animate) return;
        this.started = true;
        this.reduced = win.matchMedia('(prefers-reduced-motion: reduce)');
        this.desktop = win.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
        this.readTokens();
        this.observer = new win.IntersectionObserver((entries) => {
            if (this.reduced.matches || this.document.hidden) return;
            const groups = new Map();
            entries.forEach(({ target, isIntersecting }) => {
                if (!isIntersecting) return;
                const group = target.closest('[data-ov-motion-group]') || target;
                const index = groups.get(group) || 0;
                groups.set(group, index + 1);
                this.play(target, Math.min(index, 3) * this.tokens.stagger);
            });
        }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });
        this.parallaxObserver = new win.IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (isIntersecting) this.activeFrames.add(target);
                else this.activeFrames.delete(target);
            });
            this.scheduleFrame();
        }, { rootMargin: '80px 0px' });

        this.reduced.addEventListener('change', this.onPreference);
        this.desktop.addEventListener('change', this.onResize);
        this.document.addEventListener('focusin', this.onFocus);
        this.document.addEventListener('visibilitychange', this.onVisibility);
        win.addEventListener('scroll', this.scheduleFrame, { passive: true });
        win.addEventListener('resize', this.onResize, { passive: true });
        this.scan(this.document, true);

        // Catalog pagination and reviews insert fresh server-rendered components.
        if (win.MutationObserver) {
            this.mutations = new win.MutationObserver((records) => {
                records.forEach(({ addedNodes }) => addedNodes.forEach((node) => {
                    if (node.nodeType === 1) this.scan(node);
                }));
                this.reveals.forEach((state, element) => { if (!element.isConnected) this.release(element); });
                this.frames.forEach((image, frame) => {
                    if (frame.isConnected) return;
                    image.style.removeProperty('--ov-parallax-y');
                    this.parallaxObserver.unobserve(frame);
                    this.frames.delete(frame);
                    this.activeFrames.delete(frame);
                });
            });
            this.mutations.observe(this.document.getElementById('main-content') || this.document.body, { childList: true, subtree: true });
        }
    }

    readTokens() {
        const styles = this.window.getComputedStyle(this.document.body);
        const number = (name, fallback) => parseFloat(styles.getPropertyValue(`--ov-motion-${name}`)) || fallback;
        this.tokens = {
            duration: number('duration', 800),
            stagger: number('stagger', 75),
            distance: number('distance', 28),
            parallax: number('parallax', 18),
            ease: styles.getPropertyValue('--ov-motion-ease').trim() || 'cubic-bezier(.22, 1, .36, 1)',
        };
    }

    scan(root, initial = false) {
        if (this.reduced.matches || root.closest?.(EXCLUDED)) return;
        const elements = [...root.querySelectorAll(`${REVEAL}, ${PARALLAX}`)];
        if (root.matches?.(`${REVEAL}, ${PARALLAX}`)) elements.unshift(root);
        elements.forEach((element) => {
            if (element.closest(EXCLUDED)) return;
            if (element.matches(REVEAL)) this.prepare(element, initial);
            if (element.matches(PARALLAX) && !this.frames.has(element)) {
                const image = element.querySelector('img');
                if (image) {
                    this.frames.set(element, image);
                    this.parallaxObserver.observe(element);
                }
            }
        });
    }

    prepare(element, initial) {
        if (this.seen.has(element)) return;
        const bounds = element.getBoundingClientRect();
        // Hidden responsive content remains eligible when a resize reveals it.
        if (!bounds.width || !bounds.height) return;
        this.seen.add(element);
        if (bounds.bottom <= 0 || element.contains(this.document.activeElement)) return;
        const aboveFold = initial && bounds.top < this.window.innerHeight;
        const kind = element.dataset.ovReveal;
        const distance = aboveFold ? Math.min(this.tokens.distance, 12) : this.tokens.distance;
        const from = { opacity: aboveFold || kind === 'intro' ? 1 : 0, transform: `translate3d(0, ${distance}px, 0)` };
        const to = { opacity: 1, transform: 'translate3d(0, 0, 0)' };
        // Hero copy is baked into the artwork: keep its full frame and readable content.
        if (kind === 'hero') { from.opacity = .8; from.transform = to.transform; }
        if (!aboveFold && kind === 'heading') {
            from.clipPath = 'inset(0 0 100% 0)';
            to.clipPath = 'inset(0 0 0% 0)';
        }
        if (!aboveFold && kind === 'image') {
            from.clipPath = 'inset(4% 0 4% 0)';
            to.clipPath = 'inset(0 0 0% 0)';
        }
        const duration = this.tokens.duration * (kind === 'hero' ? 1.5 : (kind === 'image' ? 1.2 : 1));
        try {
            const animation = element.animate([from, to], { duration, easing: this.tokens.ease, fill: 'both' });
            this.reveals.set(element, { animation, playing: false });
            animation.pause();
            animation.currentTime = 0;
            animation.onfinish = () => this.release(element);
            this.observer.observe(element);
            if (aboveFold && !this.document.hidden) this.play(element);
        } catch (error) {
            // An unsupported animation must never leave shopping content hidden.
            this.release(element);
        }
    }

    play(element, delay = 0) {
        const state = this.reveals.get(element);
        if (!state || state.playing) return;
        state.playing = true;
        this.observer.unobserve(element);
        try {
            state.animation.effect.updateTiming({ delay });
            state.animation.play();
        } catch (error) {
            this.release(element);
        }
    }

    release(element) {
        const state = this.reveals.get(element);
        if (!state) return;
        state.animation.onfinish = null;
        state.animation.cancel();
        this.reveals.delete(element);
        this.observer.unobserve(element);
    }

    onFocus({ target }) {
        // Tab/anchor navigation always exposes a focused control immediately.
        this.reveals.forEach((state, element) => { if (element.contains(target)) this.release(element); });
    }

    onPreference() {
        if (this.reduced.matches) {
            this.reveals.forEach((state, element) => this.release(element));
            this.resetParallax();
        } else {
            this.scan(this.document, true);
            this.scheduleFrame();
        }
    }

    onVisibility() {
        if (this.document.hidden) {
            this.reveals.forEach((state, element) => { if (state.playing) this.release(element); });
            this.cancelFrame();
            return;
        }
        this.reveals.forEach((state, element) => {
            const bounds = element.getBoundingClientRect();
            if (bounds.top < this.window.innerHeight && bounds.bottom > 0) this.play(element);
        });
        this.scheduleFrame();
    }

    onResize() {
        this.readTokens();
        this.scan(this.document, true);
        if (!this.desktop.matches) this.resetParallax();
        else this.scheduleFrame();
    }

    scheduleFrame() {
        if (this.frameRequest !== null || this.reduced.matches || !this.desktop.matches || this.document.hidden || !this.activeFrames.size) return;
        this.frameRequest = this.window.requestAnimationFrame(() => {
            this.frameRequest = null;
            const updates = [];
            const height = this.window.innerHeight;
            this.activeFrames.forEach((frame) => {
                const image = this.frames.get(frame);
                if (!image || !frame.isConnected) return;
                const bounds = frame.getBoundingClientRect();
                const progress = Math.max(-1, Math.min(1, (height / 2 - bounds.top - bounds.height / 2) / ((height + bounds.height) / 2)));
                // Overscan is 4% per edge; travel stays inside it for every image size.
                const distance = Math.min(this.tokens.parallax, bounds.height * .03);
                updates.push([image, `${(progress * distance).toFixed(2)}px`]);
            });
            // Read every frame first, then write: one update per scroll frame, no idle loop.
            updates.forEach(([image, value]) => image.style.setProperty('--ov-parallax-y', value));
        });
    }

    cancelFrame() {
        if (this.frameRequest !== null) this.window.cancelAnimationFrame(this.frameRequest);
        this.frameRequest = null;
    }

    resetParallax() {
        this.cancelFrame();
        this.frames.forEach((image) => image.style.removeProperty('--ov-parallax-y'));
    }

    destroy() {
        if (!this.started) return;
        this.reveals.forEach((state, element) => this.release(element));
        this.resetParallax();
        this.observer.disconnect();
        this.parallaxObserver.disconnect();
        this.mutations?.disconnect();
        this.reduced.removeEventListener('change', this.onPreference);
        this.desktop.removeEventListener('change', this.onResize);
        this.document.removeEventListener('focusin', this.onFocus);
        this.document.removeEventListener('visibilitychange', this.onVisibility);
        this.window.removeEventListener('scroll', this.scheduleFrame);
        this.window.removeEventListener('resize', this.onResize);
        this.frames.clear();
        this.activeFrames.clear();
        this.started = false;
    }
}
